
import { Component, computed, ElementRef, inject, input, output, QueryList, signal, ViewChildren } from "@angular/core";
import { LucideAngularModule, X } from "lucide-angular";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";

@Component({
  selector: "app-input-search-dropdown-uif",
  imports: [LucideAngularModule, TranslocoDirective],
  templateUrl: "./input-search-dropdown-uif.component.html",
  styles: ``,
})
export class InputSearchDropdownUifComponent {
  //#region I/O
  items = input<string[]>([]);
  selection = output<string>();
  translocoService = inject(TranslocoService);
  //#endregion

  // Reference to option elements for scrolling
  @ViewChildren("optionItem") optionItems!: QueryList<ElementRef>;

  //#region Fields
  searchTerm = signal("");
  focusedIndex = signal(-1);
  isDropdownOpen = computed(() => this.searchTerm() !== "");
  filteredItems = computed(() => {
    const term = this.searchTerm().toLowerCase();
    return this.items().filter((i) => i.toLowerCase().includes(term));
  });
  isInputFocused = false;
  //#endregion
  readonly closeIcon = X;

  //#region UI Methods
  onSelect(item: string) {
    this.selection.emit(item);
    this.searchTerm.set("");
    this.focusedIndex.set(-1);
  }

  onClear() {
    this.searchTerm.set("");
    this.focusedIndex.set(-1);
  }

  onKeyDown(event: KeyboardEvent) {
    const options = this.filteredItems();
    if (!options.length) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.focusedIndex.update((i) => (i + 1) % options.length);
        this.scrollToFocusedOption();
        break;

      case "ArrowUp":
        event.preventDefault();
        this.focusedIndex.update((i) => (i - 1 + options.length) % options.length);
        this.scrollToFocusedOption();
        break;

      case "Enter": {
        event.preventDefault();
        const current = this.focusedIndex();
        const selected = current >= 0 ? options[current] : options[0];
        if (selected) {
          this.onSelect(selected);
        }
        break;
      }
    }
  }

  // Scroll to the currently focused option
  private scrollToFocusedOption(): void {
    setTimeout(() => {
      const index = this.focusedIndex();
      if (index >= 0 && this.optionItems) {
        const optionsList = this.optionItems.toArray();
        if (optionsList[index]) {
          optionsList[index].nativeElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    });
  }

  //#endregion
}
